import React from 'react';
import { useCapability, useCapabilitiesReady } from '@emilgroup/flags/react';
import { useAppLogger } from 'Services/logger';

/**
 * Loads the Segment analytics SDK at runtime, but only for tenants whose
 * `analytics` capability is enabled (resolved by the flagservice from the
 * bearer token — see CapabilitiesProvider).
 *
 * The Segment *stub* (the in-memory queue) is still installed unconditionally in
 * `public/index.html`, so every `analytics?.identify/track/page` call across the
 * app keeps working and simply queues. This component performs the actual
 * `analytics.load()` — the step that injects the CDN script and starts sending —
 * once, only when the capability is on. For a disabled tenant `load()` never
 * runs, so the queued events stay in memory and nothing leaves the browser.
 *
 * Mount it inside the authenticated subtree (under CapabilitiesProvider). It
 * renders nothing.
 */

const segmentKey: string | undefined = process.env.SEGMENT_KEY;

// Module-level guard so we load at most once per page load, even across
// re-renders or React StrictMode's double-invoked effects.
let segmentLoaded = false;

export const SegmentLoader: React.FC = () => {
  const ready = useCapabilitiesReady();
  const analyticsCapability = useCapability( 'analytics' );
  const logger = useAppLogger();

  React.useEffect( () => {
    if ( segmentLoaded || !ready || !analyticsCapability.enabled ) {
      return;
    }
    if ( !segmentKey || segmentKey === 'undefined' ) {
      // No write key configured for this build; nothing to load.
      return;
    }
    if ( !analytics ) {
      // Stub not installed (index.html only installs it when a key is present).
      return;
    }

    segmentLoaded = true;
    logger.info( 'analytics capability enabled — loading Segment' );
    analytics.load( segmentKey );
    analytics.page();
  }, [ ready, analyticsCapability.enabled, logger ] );

  return null;
};
